import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Autocomplete,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Typography,
  InputAdornment,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  ListItemText,
  ListSubheader,
  Divider,
} from "@mui/material";
import {
  Home as HomeIcon,
  DirectionsCar as CarIcon,
  FlightTakeoff as VacationIcon,
  School as EducationIcon,
  LocalHospital as EmergencyIcon,
  AccountBalance as RetirementIcon,
  Favorite as WeddingIcon,
  ChildCare as BabyIcon,
  Business as BusinessIcon,
  Devices as ElectronicsIcon,
  HealthAndSafety as HealthIcon,
  CardGiftcard as GiftIcon,
  Savings as SavingsIcon,
  TrendingUp as InvestmentIcon,
  Category as OtherIcon,
} from "@mui/icons-material";
import {
  Goal,
  GoalIcon,
  GoalLinkedAccount,
  GoalStatus,
  Debt,
  Expense,
  TargetAmountSource,
} from "../../lib/types";

// Icon mapping for display
export const GOAL_ICONS: Record<
  GoalIcon,
  { icon: React.ReactNode; label: string; color: string }
> = {
  home: { icon: <HomeIcon />, label: "Home", color: "#4CAF50" },
  car: { icon: <CarIcon />, label: "Car", color: "#2196F3" },
  vacation: { icon: <VacationIcon />, label: "Vacation", color: "#FF9800" },
  education: { icon: <EducationIcon />, label: "Education", color: "#9C27B0" },
  emergency: { icon: <EmergencyIcon />, label: "Emergency", color: "#F44336" },
  retirement: {
    icon: <RetirementIcon />,
    label: "Retirement",
    color: "#607D8B",
  },
  wedding: { icon: <WeddingIcon />, label: "Wedding", color: "#E91E63" },
  baby: { icon: <BabyIcon />, label: "Baby", color: "#FF5722" },
  business: { icon: <BusinessIcon />, label: "Business", color: "#795548" },
  electronics: {
    icon: <ElectronicsIcon />,
    label: "Electronics",
    color: "#00BCD4",
  },
  health: { icon: <HealthIcon />, label: "Health", color: "#8BC34A" },
  gift: { icon: <GiftIcon />, label: "Gift", color: "#673AB7" },
  savings: { icon: <SavingsIcon />, label: "Savings", color: "#009688" },
  investment: {
    icon: <InvestmentIcon />,
    label: "Investment",
    color: "#3F51B5",
  },
  other: { icon: <OtherIcon />, label: "Other", color: "#9E9E9E" },
};

interface GoalFormProps {
  onSubmit: (goal: Omit<Goal, "id" | "createdAt" | "updatedAt">) => void;
  onCancel?: () => void;
  goalToEdit?: Goal;
  accounts?: GoalLinkedAccount[];
  debts?: Debt[];
  expenses?: Expense[];
  categoryOptions?: string[];
}

const normalizeGoalCategories = (raw: unknown): string[] => {
  const values = Array.isArray(raw) ? raw : [];
  const cleaned = values.map((c) => String(c || "").trim()).filter(Boolean);
  const seen = new Set<string>();
  const result: string[] = [];
  for (const c of cleaned) {
    const key = c.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(c);
  }
  return result;
};

const GoalForm: React.FC<GoalFormProps> = ({
  onSubmit,
  onCancel,
  goalToEdit,
  accounts = [],
  debts = [],
  expenses = [],
  categoryOptions = [],
}) => {
  const [name, setName] = useState(goalToEdit?.name || "");
  const [description, setDescription] = useState(goalToEdit?.description || "");
  const [categories, setCategories] = useState<string[]>(
    normalizeGoalCategories(goalToEdit?.categories)
  );
  const [categoryInput, setCategoryInput] = useState("");
  const [targetAmount, setTargetAmount] = useState(
    goalToEdit?.targetAmount?.toString() || ""
  );
  const [targetDate, setTargetDate] = useState(
    goalToEdit?.targetDate
      ? new Date(goalToEdit.targetDate).toISOString().split("T")[0]
      : ""
  );
  const [icon, setIcon] = useState<GoalIcon>(goalToEdit?.icon || "savings");
  const [linkedAccountIds, setLinkedAccountIds] = useState<string[]>(
    goalToEdit?.linkedAccountIds || []
  );
  const [error, setError] = useState("");

  // Target amount source state - initialize from goalToEdit for persistence
  const [targetAmountSource, setTargetAmountSource] =
    useState<TargetAmountSource>(goalToEdit?.targetAmountSource || "custom");
  const [selectedDebtIds, setSelectedDebtIds] = useState<string[]>(
    goalToEdit?.linkedDebtIds || []
  );
  const [selectedExpenseIds, setSelectedExpenseIds] = useState<string[]>(
    goalToEdit?.linkedExpenseIds || []
  );

  // Sync state when goalToEdit changes (for when drawer stays mounted)
  useEffect(() => {
    console.log("[GoalForm] goalToEdit changed:", {
      id: goalToEdit?.id,
      name: goalToEdit?.name,
      targetAmountSource: goalToEdit?.targetAmountSource,
      linkedDebtIds: goalToEdit?.linkedDebtIds,
      linkedExpenseIds: goalToEdit?.linkedExpenseIds,
    });
    setTargetAmountSource(goalToEdit?.targetAmountSource || "custom");
    setSelectedDebtIds(goalToEdit?.linkedDebtIds || []);
    setSelectedExpenseIds(goalToEdit?.linkedExpenseIds || []);
    setCategories(normalizeGoalCategories(goalToEdit?.categories));
    setCategoryInput("");
  }, [
    goalToEdit?.id,
    goalToEdit?.linkedDebtIds,
    goalToEdit?.linkedExpenseIds,
    goalToEdit?.name,
    goalToEdit?.targetAmountSource,
    goalToEdit?.categories,
  ]);

  const getCreditTargetAmount = (account: GoalLinkedAccount) => {
    if (account.creditLimit != null && account.creditLimit > 0) {
      return account.creditLimit;
    }

    if (account.availableBalance != null) {
      return account.availableBalance + (account.balance || 0);
    }

    return account.balance || 0;
  };

  const getCreditCurrentAmount = (account: GoalLinkedAccount) => {
    if (account.availableBalance != null) {
      return account.availableBalance;
    }

    if (account.creditLimit != null && account.balance != null) {
      return Math.max(account.creditLimit - account.balance, 0);
    }

    return account.balance || 0;
  };

  const getLinkedCreditTargetAmount = () => {
    const linkedCreditAccounts = accounts.filter(
      (account) =>
        linkedAccountIds.includes(account.id) && account.type === "credit"
    );

    if (linkedCreditAccounts.length === 0) return 0;

    return linkedCreditAccounts.reduce(
      (sum, account) => sum + getCreditTargetAmount(account),
      0
    );
  };

  const linkedCreditTargetAmount = getLinkedCreditTargetAmount();

  const normalizeDebtBalance = (balance: number) =>
    Math.abs(Number.isFinite(balance) ? balance : 0);

  const formatNegativeCurrency = (amount: number) =>
    `-$${normalizeDebtBalance(amount).toLocaleString()}`;

  // Calculate target amount from selected debts (total balance)
  const getSelectedDebtsTotal = () => {
    return debts
      .filter((debt) => selectedDebtIds.includes(debt.id))
      .reduce((sum, debt) => sum + normalizeDebtBalance(debt.balance), 0);
  };

  // Calculate available credit (progress) from selected debts
  const getSelectedDebtsAvailableCredit = () => {
    return debts
      .filter((debt) => selectedDebtIds.includes(debt.id))
      .reduce((sum, debt) => {
        if (debt.creditLimit > 0) {
          // For credit accounts: available = limit - balance
          return (
            sum +
            Math.max(debt.creditLimit - normalizeDebtBalance(debt.balance), 0)
          );
        }
        // For non-credit debts (loans), progress is 0 until paid off
        return sum;
      }, 0);
  };

  // Calculate target amount from selected expenses
  const getSelectedExpensesTotal = () => {
    return expenses
      .filter((expense) => selectedExpenseIds.includes(expense.id))
      .reduce((sum, expense) => sum + expense.amount, 0);
  };

  // Update target amount when source or selections change
  useEffect(() => {
    if (targetAmountSource === "debts") {
      const selectedDebts = debts.filter((debt) =>
        selectedDebtIds.includes(debt.id)
      );
      // Target = sum of balances (always positive as a target amount)
      const total = selectedDebts.reduce(
        (sum, debt) => sum + normalizeDebtBalance(debt.balance),
        0
      );
      setTargetAmount(total > 0 ? total.toString() : "");
    } else if (targetAmountSource === "expenses") {
      const total = expenses
        .filter((expense) => selectedExpenseIds.includes(expense.id))
        .reduce((sum, expense) => sum + expense.amount, 0);
      setTargetAmount(total > 0 ? total.toString() : "");
    } else if (linkedCreditTargetAmount > 0) {
      setTargetAmount(linkedCreditTargetAmount.toString());
    }
  }, [
    targetAmountSource,
    selectedDebtIds,
    selectedExpenseIds,
    linkedCreditTargetAmount,
    debts,
    expenses,
  ]);

  // Reset selections when source changes
  const handleTargetSourceChange = (
    _event: React.MouseEvent<HTMLElement>,
    newSource: TargetAmountSource | null
  ) => {
    if (newSource !== null) {
      console.log("[GoalForm] Target amount source changed:", {
        from: targetAmountSource,
        to: newSource,
      });
      setTargetAmountSource(newSource);
      setTargetAmount("");
      if (newSource === "custom") {
        setSelectedDebtIds([]);
        setSelectedExpenseIds([]);
      } else if (newSource === "debts") {
        setSelectedExpenseIds([]);
      } else if (newSource === "expenses") {
        setSelectedDebtIds([]);
      }
    }
  };

  // Helper to get debt display name with fallbacks
  const getDebtDisplayName = (debt: Debt): string => {
    if (debt.account && debt.account.trim()) {
      return debt.account;
    }
    if (debt.creditor && debt.creditor.trim()) {
      return debt.creditor;
    }
    if (debt.type && debt.type.trim()) {
      return `${debt.type} Debt`;
    }
    return "Unnamed Debt";
  };

  // Group debts by type
  const debtsByType = useMemo(() => {
    const grouped: Record<string, typeof debts> = {};
    debts.forEach((debt) => {
      const type = debt.type || "Other";
      if (!grouped[type]) {
        grouped[type] = [];
      }
      grouped[type].push(debt);
    });
    // Sort types alphabetically
    return Object.keys(grouped)
      .sort()
      .reduce(
        (acc, key) => {
          acc[key] = grouped[key];
          return acc;
        },
        {} as Record<string, typeof debts>
      );
  }, [debts]);

  // Check if all debts are selected
  const allDebtsSelected =
    debts.length > 0 && selectedDebtIds.length === debts.length;
  const someDebtsSelected =
    selectedDebtIds.length > 0 && selectedDebtIds.length < debts.length;

  // Check if all expenses are selected
  const allExpensesSelected =
    expenses.length > 0 && selectedExpenseIds.length === expenses.length;
  const someExpensesSelected =
    selectedExpenseIds.length > 0 &&
    selectedExpenseIds.length < expenses.length;

  // Handle Select All for debts
  const handleSelectAllDebts = () => {
    if (allDebtsSelected) {
      setSelectedDebtIds([]);
    } else {
      setSelectedDebtIds(debts.map((d) => d.id));
    }
  };

  // Handle Select All for a specific debt type group
  const handleSelectAllDebtsByType = (typeDebts: Debt[]) => {
    const typeDebtIds = typeDebts.map((d) => d.id);
    const allTypeSelected = typeDebtIds.every((id) =>
      selectedDebtIds.includes(id)
    );

    if (allTypeSelected) {
      // Deselect all in this group
      setSelectedDebtIds(
        selectedDebtIds.filter((id) => !typeDebtIds.includes(id))
      );
    } else {
      // Select all in this group (add to existing selection)
      const newIds = new Set([...selectedDebtIds, ...typeDebtIds]);
      setSelectedDebtIds(Array.from(newIds));
    }
  };

  // Check selection state for a debt type group
  const getTypeSelectionState = (typeDebts: Debt[]) => {
    const typeDebtIds = typeDebts.map((d) => d.id);
    const selectedCount = typeDebtIds.filter((id) =>
      selectedDebtIds.includes(id)
    ).length;
    return {
      allSelected: selectedCount === typeDebtIds.length,
      someSelected: selectedCount > 0 && selectedCount < typeDebtIds.length,
    };
  };

  // Handle Select All for expenses
  const handleSelectAllExpenses = () => {
    if (allExpensesSelected) {
      setSelectedExpenseIds([]);
    } else {
      setSelectedExpenseIds(expenses.map((e) => e.id));
    }
  };

  // Calculate current amount (progress) based on source
  const calculateCurrentAmount = (): number => {
    // If debts are selected, use available credit as progress
    if (targetAmountSource === "debts" && selectedDebtIds.length > 0) {
      return getSelectedDebtsAvailableCredit();
    }

    // If linked accounts exist, calculate from them
    if (linkedAccountIds.length > 0) {
      return accounts
        .filter((acc) => linkedAccountIds.includes(acc.id))
        .reduce((sum, acc) => {
          if (acc.type === "credit") {
            return sum + getCreditCurrentAmount(acc);
          }
          return sum + (acc.balance || 0);
        }, 0);
    }

    return goalToEdit?.currentAmount || 0;
  };

  const submitNow = () => {
    if (!name.trim()) {
      setError("Please enter a goal name");
      return;
    }

    const resolvedTargetAmount =
      linkedCreditTargetAmount > 0
        ? linkedCreditTargetAmount
        : parseFloat(targetAmount);

    if (!resolvedTargetAmount || resolvedTargetAmount <= 0) {
      setError("Please enter a valid target amount");
      return;
    }

    if (!targetDate) {
      setError("Please select a target date");
      return;
    }

    const targetDateObj = new Date(targetDate);
    if (targetDateObj <= new Date()) {
      setError("Target date must be in the future");
      return;
    }

    setError("");

    const currentAmount = calculateCurrentAmount();
    const status: GoalStatus =
      currentAmount >= resolvedTargetAmount ? "completed" : "active";

    const submitData = {
      name: name.trim(),
      description: description.trim() || undefined,
      categories:
        categories.length > 0 ? normalizeGoalCategories(categories) : undefined,
      targetAmount: resolvedTargetAmount,
      currentAmount,
      targetDate: targetDateObj,
      icon,
      status,
      linkedAccountIds,
      linkedDebtIds: selectedDebtIds.length > 0 ? selectedDebtIds : undefined,
      linkedExpenseIds:
        selectedExpenseIds.length > 0 ? selectedExpenseIds : undefined,
      targetAmountSource,
    };
    console.log("[GoalForm] Submitting goal data:", {
      targetAmountSource,
      selectedDebtIds,
      linkedDebtIds: submitData.linkedDebtIds,
      linkedExpenseIds: submitData.linkedExpenseIds,
      categories: submitData.categories,
    });
    onSubmit(submitData);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    submitNow();
  };

  const commitCategoryInput = useCallback(() => {
    const next = categoryInput.trim().replace(/,+$/, "").trim();
    if (!next) return;
    setCategories((prev) => normalizeGoalCategories([...prev, next]));
    setCategoryInput("");
  }, [categoryInput]);

  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
      onKeyDown={(e) => {
        if (e.key !== "Enter") return;
        if (e.shiftKey || e.altKey || e.ctrlKey || e.metaKey) return;

        const target = e.target as HTMLElement | null;
        if (!target) return;

        // Allow multiline inputs (Description) to use Enter normally.
        if (target.tagName === "TEXTAREA") return;

        // If a dropdown/autocomplete is open, let Enter select options.
        const expanded = target.getAttribute("aria-expanded");
        if (expanded === "true") return;

        // If a menu item / option is being selected, don't submit.
        const role = target.getAttribute("role");
        if (role === "option" || role === "menuitem") return;

        e.preventDefault();
        submitNow();
      }}
      sx={{ p: 2 }}
    >
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <TextField
        fullWidth
        label="Goal Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g., Emergency Fund, New Car"
        required
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        label="Description (Optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="What is this goal for?"
        multiline
        rows={2}
        sx={{ mb: 2 }}
      />

      <Autocomplete
        multiple
        freeSolo
        options={categoryOptions}
        value={categories}
        inputValue={categoryInput}
        onInputChange={(_, value) => {
          if (typeof value === "string" && value.includes(",")) {
            const parts = value
              .split(",")
              .map((p) => p.trim())
              .filter(Boolean);
            if (parts.length > 0) {
              setCategories((prev) =>
                normalizeGoalCategories([...prev, ...parts])
              );
              setCategoryInput("");
              return;
            }
          }
          setCategoryInput(value);
        }}
        onChange={(_, value) => setCategories(normalizeGoalCategories(value))}
        renderTags={(value, getTagProps) =>
          value.map((option, index) => (
            <Chip
              variant="outlined"
              label={option}
              {...getTagProps({ index })}
              key={`${option}-${index}`}
            />
          ))
        }
        renderInput={(params) => (
          <TextField
            {...params}
            fullWidth
            label="Categories"
            placeholder="Type to search or add, press Enter or ,"
            sx={{ mb: 2 }}
            onKeyDown={(e) => {
              if (e.key === ",") {
                e.preventDefault();
                e.stopPropagation();
                commitCategoryInput();
                return;
              }
              if (e.key === "Enter" && categoryInput.trim()) {
                e.preventDefault();
                e.stopPropagation();
                commitCategoryInput();
              }
            }}
            onBlur={() => commitCategoryInput()}
          />
        )}
      />

      {/* Target Amount Source Selector */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Target Amount Source
        </Typography>
        <ToggleButtonGroup
          value={targetAmountSource}
          exclusive
          onChange={handleTargetSourceChange}
          size="small"
          fullWidth
          sx={{ mb: 2 }}
        >
          <ToggleButton value="custom">Custom</ToggleButton>
          <ToggleButton value="debts" disabled={debts.length === 0}>
            Debts
          </ToggleButton>
          <ToggleButton value="expenses" disabled={expenses.length === 0}>
            Expenses
          </ToggleButton>
        </ToggleButtonGroup>

        {/* Debts Multi-Select */}
        {targetAmountSource === "debts" && debts.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Debts</InputLabel>
            <Select
              multiple
              value={selectedDebtIds}
              onChange={(e) => {
                const value = e.target.value as string[];
                // Filter out the "select-all" and "select-all-{type}" pseudo-values
                setSelectedDebtIds(
                  value.filter((v) => !v.startsWith("select-all"))
                );
              }}
              input={<OutlinedInput label="Select Debts" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.length === debts.length ? (
                    <Chip
                      label={`All Debts (${debts.length})`}
                      size="small"
                      color="primary"
                    />
                  ) : (
                    selected.map((id) => {
                      const debt = debts.find((d) => d.id === id);
                      const displayName = debt ? getDebtDisplayName(debt) : id;
                      return (
                        <Chip
                          key={id}
                          label={`${displayName} (${debt?.type || "Uncategorized"}) - ${formatNegativeCurrency(
                            debt?.balance || 0
                          )}`}
                          size="small"
                        />
                      );
                    })
                  )}
                </Box>
              )}
            >
              {/* Select All Option */}
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectAllDebts();
                }}
              >
                <Checkbox
                  checked={allDebtsSelected}
                  indeterminate={someDebtsSelected}
                />
                <ListItemText
                  primary={<strong>Select All</strong>}
                  secondary={`${debts.length} debts • ${formatNegativeCurrency(
                    debts.reduce(
                      (sum, d) => sum + normalizeDebtBalance(d.balance),
                      0
                    )
                  )} total balance`}
                />
              </MenuItem>
              <Divider />
              {/* Grouped Debts by Type */}
              {Object.entries(debtsByType).map(([type, typeDebts]) => {
                const { allSelected, someSelected } =
                  getTypeSelectionState(typeDebts);
                const groupTotal = typeDebts.reduce(
                  (sum, d) => sum + normalizeDebtBalance(d.balance),
                  0
                );
                return [
                  <ListSubheader
                    key={`header-${type}`}
                    sx={{ bgcolor: "background.paper", fontWeight: 600 }}
                  >
                    {type} ({typeDebts.length})
                  </ListSubheader>,
                  <MenuItem
                    key={`select-all-${type}`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleSelectAllDebtsByType(typeDebts);
                    }}
                    sx={{ pl: 4, bgcolor: "action.hover" }}
                  >
                    <Checkbox
                      checked={allSelected}
                      indeterminate={someSelected}
                    />
                    <ListItemText
                      primary={<em>Select All {type}</em>}
                      secondary={`${formatNegativeCurrency(groupTotal)} total balance`}
                    />
                  </MenuItem>,
                  ...typeDebts.map((debt) => {
                    const absBalance = normalizeDebtBalance(debt.balance);
                    const availableCredit =
                      debt.creditLimit > 0
                        ? Math.max(debt.creditLimit - absBalance, 0)
                        : 0;
                    return (
                      <MenuItem key={debt.id} value={debt.id} sx={{ pl: 6 }}>
                        <Checkbox checked={selectedDebtIds.includes(debt.id)} />
                        <ListItemText
                          primary={getDebtDisplayName(debt)}
                          secondary={
                            debt.creditLimit > 0
                              ? `Limit: $${debt.creditLimit.toLocaleString()} • Balance: ${formatNegativeCurrency(
                                  debt.balance
                                )} • Available: $${availableCredit.toLocaleString()}`
                              : `Balance: ${formatNegativeCurrency(debt.balance)}`
                          }
                        />
                      </MenuItem>
                    );
                  }),
                ];
              })}
            </Select>
            {selectedDebtIds.length > 0 && (
              <Box sx={{ mt: 0.5 }}>
                <Typography variant="caption" color="primary" display="block">
                  {selectedDebtIds.length} selected • Balance Total: -
                  {`$${getSelectedDebtsTotal().toLocaleString()}`}
                </Typography>
                <Typography
                  variant="caption"
                  color="success.main"
                  display="block"
                >
                  Available Credit (Progress): $
                  {getSelectedDebtsAvailableCredit().toLocaleString()}
                </Typography>
              </Box>
            )}
          </FormControl>
        )}

        {/* Expenses Multi-Select */}
        {targetAmountSource === "expenses" && expenses.length > 0 && (
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Select Expenses</InputLabel>
            <Select
              multiple
              value={selectedExpenseIds}
              onChange={(e) => {
                const value = e.target.value as string[];
                // Filter out the "select-all" pseudo-value
                setSelectedExpenseIds(value.filter((v) => v !== "select-all"));
              }}
              input={<OutlinedInput label="Select Expenses" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.length === expenses.length ? (
                    <Chip
                      label={`All Expenses (${expenses.length})`}
                      size="small"
                      color="primary"
                    />
                  ) : (
                    selected.map((id) => {
                      const expense = expenses.find((e) => e.id === id);
                      return (
                        <Chip
                          key={id}
                          label={`${expense?.name || id} - $${expense?.amount.toLocaleString() || 0}`}
                          size="small"
                        />
                      );
                    })
                  )}
                </Box>
              )}
            >
              {/* Select All Option */}
              <MenuItem
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleSelectAllExpenses();
                }}
              >
                <Checkbox
                  checked={allExpensesSelected}
                  indeterminate={someExpensesSelected}
                />
                <ListItemText
                  primary={<strong>Select All</strong>}
                  secondary={`${expenses.length} expenses • $${expenses.reduce((sum, e) => sum + e.amount, 0).toLocaleString()} total`}
                />
              </MenuItem>
              <Divider />
              {/* Expense Items */}
              {expenses.map((expense) => (
                <MenuItem key={expense.id} value={expense.id}>
                  <Checkbox checked={selectedExpenseIds.includes(expense.id)} />
                  <ListItemText
                    primary={expense.name}
                    secondary={`${expense.vendor} • $${expense.amount.toLocaleString()}`}
                  />
                </MenuItem>
              ))}
            </Select>
            {selectedExpenseIds.length > 0 && (
              <Typography variant="caption" color="primary" sx={{ mt: 0.5 }}>
                {selectedExpenseIds.length} selected • Total: $
                {getSelectedExpensesTotal().toLocaleString()}
              </Typography>
            )}
          </FormControl>
        )}

        {/* Target Amount Field */}
        <TextField
          fullWidth
          label="Target Amount"
          type="number"
          value={targetAmount}
          onChange={(e) => setTargetAmount(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start">$</InputAdornment>,
            readOnly:
              targetAmountSource !== "custom" || linkedCreditTargetAmount > 0,
          }}
          inputProps={{ min: 0, step: 0.01 }}
          required
        />
        {linkedCreditTargetAmount > 0 && targetAmountSource === "custom" && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Target amount is based on linked credit card available credit and
            limit.
          </Typography>
        )}
        {targetAmountSource === "debts" && selectedDebtIds.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Target amount calculated from {selectedDebtIds.length} selected debt
            {selectedDebtIds.length > 1 ? "s" : ""}.
          </Typography>
        )}
        {targetAmountSource === "expenses" && selectedExpenseIds.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Target amount calculated from {selectedExpenseIds.length} selected
            expense{selectedExpenseIds.length > 1 ? "s" : ""}.
          </Typography>
        )}
      </Box>

      <TextField
        fullWidth
        label="Target Date"
        type="date"
        value={targetDate}
        onChange={(e) => setTargetDate(e.target.value)}
        InputLabelProps={{ shrink: true }}
        required
        sx={{ mb: 2 }}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Icon</InputLabel>
        <Select
          value={icon}
          onChange={(e) => setIcon(e.target.value as GoalIcon)}
          label="Icon"
          renderValue={(selected) => (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Avatar
                sx={{
                  width: 24,
                  height: 24,
                  bgcolor: GOAL_ICONS[selected].color,
                }}
              >
                {React.cloneElement(
                  GOAL_ICONS[selected].icon as React.ReactElement<{
                    style?: React.CSSProperties;
                  }>,
                  { style: { fontSize: 16 } }
                )}
              </Avatar>
              {GOAL_ICONS[selected].label}
            </Box>
          )}
        >
          {(Object.keys(GOAL_ICONS) as GoalIcon[]).map((key) => (
            <MenuItem key={key} value={key}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Avatar
                  sx={{
                    width: 24,
                    height: 24,
                    bgcolor: GOAL_ICONS[key].color,
                  }}
                >
                  {React.cloneElement(
                    GOAL_ICONS[key].icon as React.ReactElement<{
                      style?: React.CSSProperties;
                    }>,
                    { style: { fontSize: 16 } }
                  )}
                </Avatar>
                {GOAL_ICONS[key].label}
              </Box>
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {accounts.length > 0 && (
        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel>Link Accounts (Optional)</InputLabel>
          <Select
            multiple
            value={linkedAccountIds}
            onChange={(e) => setLinkedAccountIds(e.target.value as string[])}
            input={<OutlinedInput label="Link Accounts (Optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((id) => {
                  const account = accounts.find((a) => a.id === id);
                  return (
                    <Chip key={id} label={account?.name || id} size="small" />
                  );
                })}
              </Box>
            )}
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <span>{account.name}</span>
                    {(account.type || account.subtype) && (
                      <Typography variant="caption" color="text.secondary">
                        {[account.type, account.subtype]
                          .filter(Boolean)
                          .join(" • ")}
                        {account.mask ? ` • ${account.mask}` : ""}
                      </Typography>
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    ${account.balance.toLocaleString()}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
            Link accounts to automatically track progress based on account
            balances
          </Typography>
        </FormControl>
      )}

      <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
        {onCancel && (
          <Button variant="outlined" onClick={onCancel} fullWidth>
            Cancel
          </Button>
        )}
        <Button type="submit" variant="contained" fullWidth>
          {goalToEdit ? "Update Goal" : "Create Goal"}
        </Button>
      </Box>
    </Box>
  );
};

export default GoalForm;
